import React, { useState, useEffect } from "react";
import config from "../../config/config";

const CareerFormData = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filters, setFilters] = useState({
    status: "all",
    position: "all",
    dateFrom: "",
    dateTo: "",
    searchTerm: "",
  });

  // Fetch applications on component mount
  useEffect(() => {
    fetchApplications();
  }, []);

  // Fetch career applications
  const fetchApplications = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        status: filters.status,
        position: filters.position,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        search: filters.searchTerm,
      });

      const response = await fetch(
        config.getAdminApiUrl(`get_career_applications.php?${queryParams}`),
        {
          headers: {
            "X-API-Key": "ncg_admin_2024",
          },
        }
      );
      const data = await response.json();

      if (data.success) {
        setApplications(data.applications);
      }
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  // Apply filters
  const applyFilters = () => {
    fetchApplications();
  };

  // Handle status update
  const handleStatusUpdate = async (applicationId, newStatus) => {
    try {
      setLoading(true);
      const response = await fetch(config.getAdminApiUrl("update_career_status.php"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": "ncg_admin_2024",
        },
        body: JSON.stringify({
          id: applicationId,
          status: newStatus,
        }),
      });

      const data = await response.json();

      if (data.success) {
        fetchApplications();
        alert("Status updated successfully!");
      } else {
        alert("Error updating status: " + data.message);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Error updating status");
    } finally {
      setLoading(false);
    }
  };

  // Handle application deletion
  const handleDelete = async (applicationId) => {
    if (!window.confirm("Are you sure you want to delete this application?"))
      return;

    try {
      setLoading(true);
      const response = await fetch(
        config.getAdminApiUrl("delete_career_application.php"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": "ncg_admin_2024",
          },
          body: JSON.stringify({ id: applicationId }),
        }
      );

      const data = await response.json();

      if (data.success) {
        fetchApplications();
        alert("Application deleted successfully!");
      } else {
        alert("Error deleting application: " + data.message);
      }
    } catch (error) {
      console.error("Error deleting application:", error);
      alert("Error deleting application");
    } finally {
      setLoading(false);
    }
  };

  // Show application details
  const showDetails = (application) => {
    setSelectedApplication(application);
    setShowDetailsModal(true);
  };

  // Download resume
  const downloadResume = (filename, applicantName) => {
    if (!filename) {
      alert("No resume file available for download");
      return;
    }
    
    const baseUrl = config.getApiUrl("download_resume.php?file=");
    const link = document.createElement("a");
    link.href = baseUrl + encodeURIComponent(filename);
    link.download = `${applicantName}_Resume.${filename.split('.').pop()}`;
    link.target = "_blank";
    link.click();
  };

  // Export applications to CSV
  const exportToCSV = () => {
    if (applications.length === 0) {
      alert("No data to export");
      return;
    }

    const headers = [
      "Name",
      "Email",
      "Phone",
      "Position",
      "Experience",
      "Resume File",
      "Status",
      "Applied At",
    ];
    const csvContent = [
      headers.join(","),
      ...applications.map((app) =>
        [
          `"${app.name}"`,
          `"${app.email}"`,
          `"${app.phone || ""}"`,
          `"${app.position}"`,
          `"${app.experience || ""}"`,
          `"${app.resume_filename || ""}"`,
          `"${app.status}"`,
          `"${new Date(app.created_at).toLocaleString()}"`,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `career_applications_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Get unique positions for filter
  const uniquePositions = [...new Set(applications.map((app) => app.position))];

  return (
    <div className="career-form-data">
      <div className="manager-header">
        <h2>Career Applications</h2>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={exportToCSV}>
            <i className="fas fa-download me-2"></i>
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="manager-content">
        <div className="filters-section">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="reviewed">Reviewed</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="interviewed">Interviewed</option>
                <option value="hired">Hired</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Position</label>
              <select
                name="position"
                value={filters.position}
                onChange={handleFilterChange}
              >
                <option value="all">All Positions</option>
                {uniquePositions.map((position) => (
                  <option key={position} value={position}>
                    {position}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>From Date</label>
              <input
                type="date"
                name="dateFrom"
                value={filters.dateFrom}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <label>To Date</label>
              <input
                type="date"
                name="dateTo"
                value={filters.dateTo}
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <label>Search</label>
              <input
                type="text"
                name="searchTerm"
                value={filters.searchTerm}
                onChange={handleFilterChange}
                placeholder="Search name, email, or position..."
              />
            </div>

            <div className="filter-group">
              <label>&nbsp;</label>
              <button className="btn btn-primary" onClick={applyFilters}>
                <i className="fas fa-search me-2"></i>
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        {/* Applications Table */}
        {loading ? (
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i> Loading...
          </div>
        ) : (
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Position</th>
                  <th>Experience</th>
                  <th>Resume</th>
                  <th>Status</th>
                  <th>Applied</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.length > 0 ? (
                  applications.map((application) => (
                    <tr key={application.id}>
                      <td>{application.name}</td>
                      <td>
                        <a href={`mailto:${application.email}`}>
                          {application.email}
                        </a>
                      </td>
                      <td>{application.phone || "—"}</td>
                      <td>{application.position}</td>
                      <td>
                        {application.experience
                          ? `${application.experience} years`
                          : "—"}
                      </td>
                      <td>
                        {application.resume_filename ? (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                              downloadResume(
                                application.resume_filename,
                                application.name
                              )
                            }
                            title="Download Resume"
                          >
                            <i className="fas fa-download me-1"></i>
                            Download
                          </button>
                        ) : (
                          <span className="text-muted">No Resume</span>
                        )}
                      </td>
                      <td>
                        <select
                          value={application.status}
                          onChange={(e) =>
                            handleStatusUpdate(application.id, e.target.value)
                          }
                          className={`status-select ${application.status}`}
                        >
                          <option value="new">New</option>
                          <option value="reviewed">Reviewed</option>
                          <option value="shortlisted">Shortlisted</option>
                          <option value="interviewed">Interviewed</option>
                          <option value="hired">Hired</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </td>
                      <td>
                        {new Date(application.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => showDetails(application)}
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(application.id)}
                            title="Delete"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No applications found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Statistics */}
        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">
                {applications.filter((a) => a.status === "new").length}
              </div>
              <div className="stat-label">New</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {applications.filter((a) => a.status === "reviewed").length}
              </div>
              <div className="stat-label">Reviewed</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {applications.filter((a) => a.status === "shortlisted").length}
              </div>
              <div className="stat-label">Shortlisted</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">
                {applications.filter((a) => a.status === "hired").length}
              </div>
              <div className="stat-label">Hired</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{applications.length}</div>
              <div className="stat-label">Total</div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedApplication && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <div className="modal-header">
              <h3>Career Application Details</h3>
              <button
                className="modal-close"
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedApplication(null);
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="application-details">
                <div className="detail-row">
                  <strong>Name:</strong>
                  <span>{selectedApplication.name}</span>
                </div>
                <div className="detail-row">
                  <strong>Email:</strong>
                  <span>
                    <a href={`mailto:${selectedApplication.email}`}>
                      {selectedApplication.email}
                    </a>
                  </span>
                </div>
                {selectedApplication.phone && (
                  <div className="detail-row">
                    <strong>Phone:</strong>
                    <span>
                      <a href={`tel:${selectedApplication.phone}`}>
                        {selectedApplication.phone}
                      </a>
                    </span>
                  </div>
                )}
                <div className="detail-row">
                  <strong>Position:</strong>
                  <span>{selectedApplication.position}</span>
                </div>
                {selectedApplication.experience && (
                  <div className="detail-row">
                    <strong>Experience:</strong>
                    <span>{selectedApplication.experience} years</span>
                  </div>
                )}
                {selectedApplication.education && (
                  <div className="detail-row">
                    <strong>Education:</strong>
                    <span>{selectedApplication.education}</span>
                  </div>
                )}
                {selectedApplication.skills && (
                  <div className="detail-row">
                    <strong>Skills:</strong>
                    <span>{selectedApplication.skills}</span>
                  </div>
                )}
                <div className="detail-row">
                  <strong>Status:</strong>
                  <span
                    className={`status-badge ${selectedApplication.status}`}
                  >
                    {selectedApplication.status}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Applied:</strong>
                  <span>
                    {new Date(selectedApplication.created_at).toLocaleString()}
                  </span>
                </div>
                {selectedApplication.cover_letter && (
                  <div className="detail-row full-width">
                    <strong>Cover Letter:</strong>
                    <div className="message-content">
                      {selectedApplication.cover_letter}
                    </div>
                  </div>
                )}
                {selectedApplication.resume_file && (
                  <div className="detail-row">
                    <strong>Resume:</strong>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() =>
                        downloadResume(
                          selectedApplication.resume_file,
                          selectedApplication.name
                        )
                      }
                    >
                      <i className="fas fa-download me-2"></i>
                      Download Resume
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedApplication(null);
                }}
              >
                Close
              </button>
              <a
                href={`mailto:${selectedApplication.email}?subject=Re: Your application for ${selectedApplication.position}`}
                className="btn btn-primary"
              >
                <i className="fas fa-reply me-2"></i>
                Reply via Email
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareerFormData;
